import React, { useMemo, useState } from "react";
import { useAuth } from "./auth";
import { deleteReportItem, moveReportItem, updateReportItem } from "./reports/reportStore";
import { REPORT_ITEM_LIMIT, getAllowedOrientations, getReportTypeLabel, supportsOrientationToggle } from "./reports/reportTypes";
import { validateReportItemAccess } from "./reports/permissions";
import { downloadReportBlob, requestReportPdf } from "./reports/reportExportApi";
import { useReportWorkspace } from "./reports/useReportWorkspace";
import ReportPreview from "./reports/renderers/ReportPreview";
import "./reports/styles.css";

function OrientationSelect({ item, disabled, onChange }) {
  const orientations = getAllowedOrientations(item.type);
  if (!supportsOrientationToggle(item.type)) {
    return <span className="reports-badge">{item.orientation === "landscape" ? "Na šířku" : "Na výšku"}</span>;
  }
  return (
    <select
      className="reports-chip-button"
      value={item.orientation}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      aria-label="Orientace reportu"
    >
      {orientations.map((orientation) => (
        <option key={orientation} value={orientation}>
          {orientation === "landscape" ? "Na šířku" : "Na výšku"}
        </option>
      ))}
    </select>
  );
}

function ReportPreviewModal({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="reports-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="reports-modal" style={{ width: "min(980px, 100%)" }} role="dialog" aria-modal="true" aria-label="Náhled reportu" onMouseDown={(event) => event.stopPropagation()}>
        <div className="reports-modal-header">
          <div>
            <div className="reports-eyebrow">Náhled PDF stránky</div>
            <h2>{item.title}</h2>
          </div>
          <button type="button" className="reports-icon-button" onClick={onClose} aria-label="Zavřít">x</button>
        </div>
        <ReportPreview item={item} mode="full" />
      </div>
    </div>
  );
}

function ReportCard({ item, index, total, access, auth, onOpen }) {
  const disabled = !access.ok;
  const update = (patch) => updateReportItem(auth.user?.id, auth.currentAreaId, item.id, patch);
  return (
    <article className={`reports-card${disabled ? " is-invalid" : ""}`}>
      <ReportPreview item={item} mode="thumbnail" />
      <div className="reports-card-meta">
        <span className="reports-badge">{getReportTypeLabel(item.type)}</span>
        <span className="reports-badge">{item.orientation === "landscape" ? "Na šířku" : "Na výšku"}</span>
      </div>
      {disabled ? <div className="reports-invalid-banner">{access.reason}</div> : null}
      <input
        className="reports-card-title"
        value={item.title}
        onChange={(event) => update({ title: event.target.value })}
        aria-label="Název položky"
      />
      <textarea
        className="reports-card-note"
        value={item.note}
        onChange={(event) => update({ note: event.target.value })}
        placeholder="Popisek"
        aria-label="Popisek položky"
      />
      <div className="reports-card-footer">
        <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: 800, color: "#334155" }}>
          <input
            type="checkbox"
            checked={item.includeInExport}
            onChange={(event) => update({ includeInExport: event.target.checked })}
          />
          Zahrnout
        </label>
        <OrientationSelect item={item} disabled={disabled} onChange={(orientation) => update({ orientation })} />
      </div>
      <div className="reports-card-actions">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span className="reports-badge" title="Reorder handle">::</span>
          <button type="button" className="reports-chip-button" onClick={() => moveReportItem(auth.user?.id, auth.currentAreaId, item.id, "up")} disabled={index === 0}>
            Nahoru
          </button>
          <button type="button" className="reports-chip-button" onClick={() => moveReportItem(auth.user?.id, auth.currentAreaId, item.id, "down")} disabled={index === total - 1}>
            Dolů
          </button>
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button type="button" className="reports-secondary-button" onClick={() => onOpen(item)} disabled={disabled}>
            Náhled
          </button>
          <button type="button" className="reports-danger-button" onClick={() => deleteReportItem(auth.user?.id, auth.currentAreaId, item.id)}>
            Smazat
          </button>
        </div>
      </div>
    </article>
  );
}

export default function ReportsPage() {
  const auth = useAuth();
  const workspace = useReportWorkspace(auth.user?.id, auth.currentAreaId);
  const [previewItemId, setPreviewItemId] = useState("");
  const [exportState, setExportState] = useState({ status: "idle", kind: "", message: "" });
  const items = workspace.items;
  const accessById = useMemo(() => {
    const map = new Map();
    items.forEach((item) => map.set(item.id, validateReportItemAccess(item, auth)));
    return map;
  }, [auth, items]);
  const previewItem = items.find((item) => item.id === previewItemId) || null;
  const previewAccess = previewItem ? accessById.get(previewItem.id) : null;
  const includedItems = items.filter((item) => item.includeInExport);
  const invalidIncludedItems = includedItems.filter((item) => !accessById.get(item.id)?.ok);
  const exportableItems = includedItems.filter((item) => accessById.get(item.id)?.ok);
  const includedCount = exportableItems.length;
  const isExporting = exportState.status === "loading";
  const canExport = exportableItems.length > 0 && invalidIncludedItems.length === 0 && !isExporting;

  const runPdfExport = async () => {
    if (!canExport) return;
    setExportState({ status: "loading", kind: "", message: "Generuji PDF..." });
    try {
      const result = await requestReportPdf({
        areaId: auth.currentAreaId,
        title: `ElectionMap report${auth.currentAreaName ? ` - ${auth.currentAreaName}` : ""}`,
        items: exportableItems,
      });
      downloadReportBlob(result.blob, result.filename);
      setExportState({ status: "success", kind: "", message: "PDF bylo vygenerováno a stahování začalo." });
    } catch (error) {
      const isValidation = error && error.kind === "validation";
      setExportState({
        status: "error",
        kind: isValidation ? "validation" : "runtime",
        message: isValidation
          ? `Export nelze vygenerovat: ${error.message || "zkontrolujte oprávnění a položky reportu."}`
          : `Exportér dočasně selhal: ${error.message || "zkuste to prosím znovu."}`,
      });
    }
  };

  return (
    <div className="reports-page">
      <header className="reports-page-header">
        <div className="reports-toolbar">
          <span className="reports-badge">{items.length}/{REPORT_ITEM_LIMIT} položek</span>
          <span className="reports-badge">{includedCount} zahrnuto</span>
          <button
            type="button"
            className="reports-primary-button"
            disabled={!canExport}
            onClick={runPdfExport}
            title={
              invalidIncludedItems.length > 0
                ? "Některé zahrnuté položky už nejsou pro aktuální oprávnění dostupné."
                : exportableItems.length === 0
                  ? "Vyberte alespoň jednu dostupnou položku."
                  : "Vygenerovat PDF"
            }
          >
            {isExporting ? "Generuji PDF..." : "Vygenerovat PDF"}
          </button>
        </div>
      </header>
      {invalidIncludedItems.length > 0 ? (
        <div className="reports-export-message is-validation">
          Export je zablokovaný, protože některé zahrnuté položky už nejsou dostupné pro aktuální oprávnění nebo aktuální oblast.
        </div>
      ) : null}
      {exportState.message ? (
        <div className={`reports-export-message is-${exportState.status}${exportState.kind ? ` is-${exportState.kind}` : ""}`}>
          {exportState.message}
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="reports-empty">
          Zatím tu není žádná položka. Uložte mapu z modulů Mapa, Tsunami nebo Místa tlačítkem “Uložit do reportu”.
        </div>
      ) : (
        <section className="reports-grid" aria-label="Uložené položky reportu">
          {items.map((item, index) => (
            <ReportCard
              key={item.id}
              item={item}
              index={index}
              total={items.length}
              auth={auth}
              access={accessById.get(item.id) || { ok: false, reason: "Položku se nepodařilo ověřit." }}
              onOpen={(candidate) => {
                const candidateAccess = accessById.get(candidate.id);
                if (candidateAccess && candidateAccess.ok) setPreviewItemId(candidate.id);
              }}
            />
          ))}
        </section>
      )}

      {previewItem && previewAccess?.ok ? <ReportPreviewModal item={previewItem} onClose={() => setPreviewItemId("")} /> : null}
    </div>
  );
}
